// hooks/useSync.js - Hook para sincronización con React
import { useState, useEffect, useCallback, useRef } from 'react';
import { syncService } from '../helpers/syncService';
import { postObservacion, putObservacion, deleteObservacion } from '../api/observacionesElementos';
import { postComentario, putComentario, deleteComentario  } from '../api/comentariosElementos';

  // ============================================================================
  // SINGLETON ONLINE STATUS
  // ============================================================================


  let _isOnline = false;
  let _listeners = new Set();

  const setGlobalOnline = (value) => {
    _isOnline = value;
    _listeners.forEach(fn => fn(value));
  };

  // Un único checker que corre a nivel de módulo
  let _retryTimer = null;
  let _checking = false;

  const checkRealConnection = async () => {
    if (_checking) return;
    _checking = true;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      const response = await fetch(
        `https://gestion.jnegre.com:44343/api/sync/health`,
        { method: 'HEAD', cache: 'no-store', signal: controller.signal }
      );
      clearTimeout(timeout);
      if (response.ok) {
        setGlobalOnline(true);
        if (_retryTimer) { clearTimeout(_retryTimer); _retryTimer = null; }
      } else {
        setGlobalOnline(false);
        scheduleRetry();
      }
    } catch {
      setGlobalOnline(false);
      scheduleRetry();
    } finally {
      _checking = false;
    }
  };

  const scheduleRetry = () => {
    if (_retryTimer) clearTimeout(_retryTimer);
    _retryTimer = setTimeout(() => {
      if (navigator.onLine) checkRealConnection();
    }, 30000);
  };

  // Inicializar listeners de red una sola vez
  window.addEventListener('online', () => checkRealConnection());
  window.addEventListener('offline', () => {
    setGlobalOnline(false);
    if (_retryTimer) { clearTimeout(_retryTimer); _retryTimer = null; }
  });

  // Check inicial
  if (navigator.onLine) checkRealConnection();

  export function useSync() {
    const [syncing, setSyncing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [lastSync, setLastSync] = useState(null);
    const [error, setError] = useState(null);

    // Cargar fecha de última sincronización
    useEffect(() => {
      const lastSyncDate = syncService.getLastSyncDate();
      setLastSync(lastSyncDate);
    }, []);

  // ============================================================================
  // SINCRONIZACIÓN INICIAL
  // ============================================================================

  const initialSync = useCallback(async () => {
  setSyncing(true);
  setError(null);
  setProgress({ current: 0, total: 0, percentage: 0 });

  let Swal;
  try {
    Swal = (await import('sweetalert2')).default;
  } catch (e) {
    Swal = null;
  }

  if (Swal) {
    // Abrimos el modal SIN await — se queda abierto indefinidamente
    // porque showConfirmButton:false y allowOutsideClick:false
    Swal.fire({
      title: 'Cargando datos del servidor',
      html: `
        <div style="text-align:left">
          <div style="margin-bottom:8px"><strong class="sw-progress-tab">Preparando descarga...</strong></div>
          <div style="width:100%;background:#eee;border-radius:8px;overflow:hidden;height:18px">
            <div class="sw-progress-bar-fill" style="width:0%;height:100%;background:#4caf50;transition:width .2s"></div>
          </div>
          <div style="margin-top:8px;font-size:12px;color:#555">
            <span class="sw-progress-text">0 / 0</span> (<span class="sw-progress-percent">0</span>%)
          </div>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
    // Pequeña pausa SOLO para que el DOM del modal exista antes del primer callback
    await new Promise(resolve => setTimeout(resolve, 50));
  }

  const TABLA_NOMBRES = {
    'SYS_Usuarios': 'Usuarios',
    'SYS_Perfiles': 'Perfiles de usuario',
    'GES_Clientes': 'Clientes',
    'GES_Analisis': 'Tipos de análisis',
    'GES_ElementosPlanta': 'Elementos de planta',
    'GES_Tareas': 'Tareas',
    'GES_ComentariosElementos': 'Comentarios',
    'GES_ObservacionesElementos': 'Observaciones',
    'GES_OfertasClientes': 'Ofertas',
    'GES_ParametrosAnalisisPlanta': 'Parámetros de análisis',
    'GES_ParametrosElementoPlantaCliente': 'Parámetros de planta',
    'GES_ValorParametros': 'Valores de parámetros',
    'GES_Conf_Analisis_NivelesPlantasCliente': 'Configuración de niveles',
    'GES_Conf_NivelesPlantasCliente': 'Niveles de planta',
    'GES_Conf_PlantasCliente': 'Plantas',
    'LST_Parametros': 'Lista de parámetros',
  };

  // La sync corre con el modal ya abierto y visible
  const result = await syncService.initialSync((progressData) => {
    const newProgress = {
      current: progressData.processed,
      total: progressData.total,
      percentage: progressData.percentage,
      tabla: TABLA_NOMBRES[progressData.tabla] || progressData.tabla
    };
    setProgress(newProgress);

    if (Swal) {
      try {
        const container = Swal.getHtmlContainer();
        if (container) {
          const fill = container.querySelector('.sw-progress-bar-fill');
          const txt  = container.querySelector('.sw-progress-text');
          const pct  = container.querySelector('.sw-progress-percent');
          const tab  = container.querySelector('.sw-progress-tab');
          if (fill) fill.style.width = `${newProgress.percentage || 0}%`;
          if (txt)  txt.textContent = `${newProgress.current} / ${newProgress.total}`;
          if (pct)  pct.textContent = `${newProgress.percentage || 0}`;
          if (tab)  tab.textContent = newProgress.tabla || 'Procesando...';
        }
      } catch (e) {}
    }
  });

  // Sync acabada — ahora sí cerramos
  if (Swal) {
    try { Swal.close(); } catch (e) {}
  }

  if (result.success) {
    setLastSync(new Date());
    setProgress(null);
  } else {
    setError(result.error);
  }

  setSyncing(false);
  return result;
}, []);
  // ============================================================================
  // SINCRONIZACIÓN INCREMENTAL
  // ============================================================================

  const incrementalSync = useCallback(async () => {
    setSyncing(true);
    setError(null);

    const result = await syncService.incrementalSync();

    if (result.success) {
      const newLastSync = new Date();
      setLastSync(newLastSync);
    } else {
      setError(result.error);
    }

    setSyncing(false);
    return result;
  }, []);

  // ============================================================================
  // SINCRONIZAR TABLA ESPECÍFICA
  // ============================================================================

  const syncTable = useCallback(async (tableName) => {
    setSyncing(true);
    setError(null);

    const result = await syncService.syncTable(tableName);

    if (result.success) {
      setLastSync(new Date());
    } else {
      setError(result.error);
    }

    setSyncing(false);
    return result;
  }, []);

  // ============================================================================
  // SINCRONIZACIÓN AUTOMÁTICA
  // ============================================================================

  const startAutoSync = useCallback((intervalMinutes = 5) => {
    return syncService.startAutoSync(intervalMinutes);
  }, []);

  const stopAutoSync = useCallback(() => {
    syncService.stopAutoSync();
  }, []);

  // ============================================================================
  // LIMPIAR BASE DE DATOS LOCAL
  // ============================================================================

  const clearLocalDatabase = useCallback(async () => {
    const result = await syncService.limpiarBaseDatosLocal();
    if (result.success) {
      setLastSync(null);
    }
    return result;
  }, []);

  return {
    syncing,
    progress,
    lastSync,
    error,
    initialSync,
    incrementalSync,
    syncTable,
    startAutoSync,
    stopAutoSync,
    clearLocalDatabase
  };
}

// ============================================================================
// HOOK PARA ESTADO ONLINE/OFFLINE
// ============================================================================


export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(_isOnline);

  useEffect(() => {
    // Suscribirse al singleton
    _listeners.add(setIsOnline);
    return () => _listeners.delete(setIsOnline);
  }, []);

  return isOnline;
}


// ============================================================================
// HOOK COMBINADO - SINCRONIZACIÓN CON DETECCIÓN ONLINE/OFFLINE
// ============================================================================

export function useAutoSync(intervalMinutes = 5) {
  const isOnline = useOnlineStatus();
  const {
    syncing,
    lastSync,
    //incrementalSync,
    startAutoSync,
    stopAutoSync
  } = useSync();

  useEffect(() => {
    if (isOnline) {
      // Iniciar sincronización automática cuando esté online
      const interval = startAutoSync(intervalMinutes);

      return () => {
        clearInterval(interval);
      };
    } else {
      // Detener sincronización cuando esté offline
      stopAutoSync();
    }
  }, [isOnline, intervalMinutes, startAutoSync, stopAutoSync]);

  return {
    isOnline,
    syncing,
    lastSync
  };
}

// ============================================================================
// HOOK PARA SINCRONIZAR CAMBIOS PENDIENTES (NUEVO)
// ============================================================================

// Lock a nivel de módulo para evitar que múltiples instancias del hook
let _globalSyncInProgress = false;

export function useSyncPendingChanges() {
  const isOnline = useOnlineStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const syncPendingChanges = useCallback(async () => {
    // 1. Validaciones iniciales
    // Usamos lock global para que dos instancias del hook no sincronicen a la vez
    if (!isOnline || isSyncing || _globalSyncInProgress) return;
    _globalSyncInProgress = true;

    const { db } = await import('../libraries/db');
    const totalPendientes = await db.table('PendingSync').count();

    if (totalPendientes === 0) {
      _globalSyncInProgress = false;
      return { success: true, synced: 0 };
    }

    setIsSyncing(true);

    try {
      const {
        putValorParametros,
        postValorParametros,
        putParametrosAnalisisPlanta,
        generarPdf,
        
      } = await import('../api');
      const Swal = (await import('sweetalert2')).default;

      Swal.fire({
        title: 'Enviando cambios al servidor',
        html: `
          <div style="text-align:left">
            <div style="margin-bottom:8px"><strong class="sw-progress-tab">Preparando...</strong></div>
            <div style="width:100%;background:#eee;border-radius:8px;overflow:hidden;height:18px">
              <div class="sw-progress-bar-fill" style="width:0%;height:100%;background:#4caf50;transition:width .2s"></div>
            </div>
            <div style="margin-top:8px;font-size:12px;color:#555">
              <span class="sw-progress-text">0 / 0</span> (<span class="sw-progress-percent">0</span>%)
            </div>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });

      await new Promise(resolve => setTimeout(resolve, 50));

      // luego sigues actualizando los selectores como antes
      document.querySelector('.sw-progress-tab').textContent = '...'

      // Obtener todos los registros (sin filtrar por synced)
      let pendientes = await db.table('PendingSync').toArray();

      // ✅ ORDENAR: Primero no-PDF, después PDF (para que la tarea esté actualizada antes de generar PDF)
      const pendientesNoPDF = pendientes.filter(p => p.type !== 'GENERAR_PDF');
      const pendientesPDF = pendientes.filter(p => p.type === 'GENERAR_PDF');
      
      const pendientesOrdenados = [...pendientesNoPDF, ...pendientesPDF];
      console.log(`📋 Procesando ${pendientesOrdenados.length} cambios (${pendientesNoPDF.length} no-PDF, ${pendientesPDF.length} PDF)`);

      let exitosos = 0;
      let fallidos = 0;

      for (let i = 0; i < pendientesOrdenados.length; i++) {
        const cambio = pendientesOrdenados[i];
        try {
          switch (cambio.type) {
            case 'PUT_VALOR_PARAMETRO':
              await putValorParametros(cambio.data);
              break;
            case 'POST_VALOR_PARAMETRO': {
              const { getFilasParametros } = await import('../api');
              const existentes = await getFilasParametros(
                cambio.data.CodigoCliente,
                cambio.data.Oferta,
                cambio.data.Id_Elemento,
                cambio.data.Id_Analisis,
                cambio.data.Fecha,
              );
              const filaExistente = existentes?.find(
                (f) => Number(f.parametro) === Number(cambio.data.Parametro) && f.fecha !== null,
              );
              let serverIdResult;
              if (filaExistente) {
                await putValorParametros({ ...cambio.data, Id: filaExistente.id });
                serverIdResult = filaExistente.id;
              } else {
                const postResult = await postValorParametros(cambio.data);
                serverIdResult = postResult?.id ?? postResult?.Id;
              }
              // Actualizar el registro local con el id del servidor
              if (serverIdResult) {
                const locales = await db.table('GES_ValorParametros').toArray();
                const local = locales.find(
                  (r) => Number(r.parametro) === Number(cambio.data.Parametro) &&
                         Number(r.id_Elemento) === Number(cambio.data.Id_Elemento) &&
                         Number(r.id_Analisis) === Number(cambio.data.Id_Analisis) &&
                         r.fecha === cambio.data.Fecha
                );
                if (local) {
                  await db.table('GES_ValorParametros').update(local.Id, { id: serverIdResult });
                }
              }
              break;
            }
            case 'PUT_PARAMETROS_ANALISIS':
              await putParametrosAnalisisPlanta(cambio.data);
              break;
            case 'POST_OBSERVACIONES_ELEMENTOS':
              const { Id: _Id, ...obsData } = cambio.data;
              await postObservacion(obsData);
              break;
            case 'POST_COMENTARIO':
              const nuevoComentario = await postComentario(cambio.data);
              const serverId = nuevoComentario?.data?.id ?? nuevoComentario?.data?.Id;
              if (serverId) {
                await db.table('PendingSync')
                  .where('type').equals('GENERAR_PDF')
                  .modify(item => {
                    if (Number(item.data.idTarea) === Number(cambio.data.idTarea)) {
                      item.data.idComentario = serverId;
                    }
                  });
              }
              break;
            case 'PUT_OBSERVACION':
              await putObservacion(cambio.data);
              break;
            case 'PUT_COMENTARIO':
              await putComentario(cambio.data);
              break;
            case 'DELETE_COMENTARIO':
              await deleteComentario(cambio.data.id);
              break;
            case 'DELETE_OBSERVACION':
              await deleteObservacion(cambio.data.id);
              break; 
            case 'GENERAR_PDF':
              const cambioActualizado = await db.table('PendingSync').get(cambio.id);
              const response = await generarPdf(cambioActualizado.data);
              console.log('✅ PDF generado con ID:', response);
              
              const tarea = await db.table('GES_ParametrosAnalisisPlanta').get(cambio.data.idTarea);
              console.log('📦 Tarea obtenida:', tarea);
              
              if (tarea) {
                const tareaActualizada = { ...tarea, pdf: response };
                console.log('📝 Tarea actualizada:', tareaActualizada);
                
                // Guardar localmente
                await db.table('GES_ParametrosAnalisisPlanta').put(tareaActualizada);
                console.log('💾 Guardado en BD local');
                
                // Enviar al backend
                console.log('🔄 Enviando PUT al backend...');
                await putParametrosAnalisisPlanta(tareaActualizada);
                console.log('✅ PUT completado en backend');
                
                // ✅ Eliminar los PUT_PARAMETROS_ANALISIS duplicados de esta misma tarea
                const duplicados = await db.table('PendingSync')
                  .where('type').equals('PUT_PARAMETROS_ANALISIS')
                  .toArray();
                
                const idsEliminar = duplicados
                  .filter(d => d.data.id === cambio.data.idTarea)
                  .map(d => d.id);
                
                if (idsEliminar.length > 0) {
                  console.log('🗑️ Eliminando duplicados:', idsEliminar);
                  await db.table('PendingSync').bulkDelete(idsEliminar);
                }
              } else {
                console.error('❌ Tarea no encontrada:', cambio.data.idTarea);
              }
              break;
            default:
              throw new Error(`Tipo desconocido en PendingSync: ${cambio.type}`);
          }

          // --- CLAVE: Borrar el registro tras éxito ---
          await db.table('PendingSync').delete(cambio.id);
          exitosos++;

          // Actualizar modal de progreso
          try {
            const container = Swal.getHtmlContainer();
            if (container) {
              const fill = container.querySelector('.sw-progress-bar-fill');
              const txt = container.querySelector('.sw-progress-text');
              const pct = container.querySelector('.sw-progress-percent');
              const tab = container.querySelector('.sw-progress-tab');
              const processed = i + 1;
              const total = pendientesOrdenados.length || 1;
              const percentage = Math.round((processed / total) * 100);
              if (fill) fill.style.width = `${percentage}%`;
              if (txt) txt.textContent = `${processed} / ${total}`;
              if (pct) pct.textContent = `${percentage}`;
              const TIPO_NOMBRES = {
                'PUT_VALOR_PARAMETRO': 'Actualizando valor de parámetro...',
                'POST_VALOR_PARAMETRO': 'Guardando nuevo valor de parámetro...',
                'PUT_PARAMETROS_ANALISIS': 'Actualizando análisis...',
                'POST_OBSERVACIONES_ELEMENTOS': 'Guardando observación...',
                'PUT_OBSERVACION': 'Actualizando observación...',
                'DELETE_OBSERVACION': 'Eliminando observación...',
                'POST_COMENTARIO': 'Guardando comentario...',
                'PUT_COMENTARIO': 'Actualizando comentario...',
                'DELETE_COMENTARIO': 'Eliminando comentario...',
                'GENERAR_PDF': 'Generando informe PDF...',
              };
              if (tab) tab.textContent = TIPO_NOMBRES[cambio.type] || 'Procesando...';
            }
          } catch (e) {}

        } catch (error) {
          console.error('Error sincronizando registro:', cambio.id, error);
          fallidos++;
          // No lo borramos, se queda en la tabla para el siguiente intento
        }
      }

      // Actualizar contador local inmediatamente
      setPendingCount(fallidos);

      if (exitosos > 0) {
        Swal.fire({
          icon: 'success',
          title: 'Datos enviados correctamente',
          text: `Se han guardado ${exitosos} ${exitosos === 1 ? 'cambio' : 'cambios'} en el servidor.`,
          timer: 2000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false
        });
        
        // ✅ RECARGAR PÁGINA DESPUÉS DE SINCRONIZACIÓN EXITOSA
        console.log('🔄 Recargando página en 2.5 segundos...');
        setTimeout(() => {
          console.log('🔄 Recargando página ahora...');
          window.location.reload();
        }, 2500);
      }

      setIsSyncing(false);
      _globalSyncInProgress = false;
      return { success: true, synced: exitosos, failed: fallidos };

    } catch (error) {
      console.error('Error crítico en sincronización:', error);
      setIsSyncing(false);
      _globalSyncInProgress = false;
      return { success: false, error };
    }
  }, [isOnline, isSyncing]);

  // Efecto para mantener el contador actualizado
  useEffect(() => {
    const updateCount = async () => {
      const { db } = await import('../libraries/db');
      const count = await db.table('PendingSync').count();
      setPendingCount(count);
    };

    updateCount();
    const interval = setInterval(updateCount, 5000); // Chequeo cada 5 seg
    return () => clearInterval(interval);
  }, []);
  
  return { isSyncing, pendingCount, syncPendingChanges };
}